import { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildSmoothPath,
  getDefinedRuns,
  getNiceScale,
} from './potential-chart.utils';

const colors = {
  deltaT: '#79B0B8',
  potential: '#E58C45',
  heatDelivered: '#E58C45',
};

const margin = {
  top: 16,
  right: 54,
  bottom: 24,
  left: 54,
};

const valueFormatter = new Intl.NumberFormat('nl-BE', {
  maximumFractionDigits: 2,
});

function formatEnergyPerYear(value) {
  if (value === undefined || value === null) return '-';

  return `${valueFormatter.format(value)} GWh/jaar`;
}

function formatTemperature(value) {
  if (value === undefined || value === null) return '-';

  return `${valueFormatter.format(value)} °C`;
}

function PotentialChart({ points, hoveredId, onHover, onClick }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const plotWidth = Math.max(0, width - margin.left - margin.right);
  const plotHeight = Math.max(0, height - margin.top - margin.bottom);

  const leftScale = useMemo(() => {
    const maxValue = points.reduce((max, point) => Math.max(
      max,
      point.potential ?? 0,
      point.heatDelivered ?? 0,
    ), 0);

    return getNiceScale(0, maxValue, 4);
  }, [points]);

  const rightScale = useMemo(() => {
    const deltaValues = points
      .map(point => point.deltaT)
      .filter(value => value !== undefined && value !== null && !Number.isNaN(value));
    const minValue = deltaValues.length ? Math.min(...deltaValues) : -1;
    const maxValue = deltaValues.length ? Math.max(...deltaValues) : 0;

    return getNiceScale(minValue, maxValue, 4);
  }, [points]);

  const getX = (localIndex) => {
    if (points.length <= 1) return margin.left + plotWidth / 2;
    return margin.left + (localIndex / (points.length - 1)) * plotWidth;
  };
  const getLeftY = (value) => {
    const span = leftScale.max - leftScale.min || 1;
    return margin.top + plotHeight - ((value - leftScale.min) / span) * plotHeight;
  };
  const getRightY = (value) => {
    const span = rightScale.max - rightScale.min || 1;
    return margin.top + plotHeight - ((value - rightScale.min) / span) * plotHeight;
  };

  const series = useMemo(() => {
    if (plotWidth <= 0 || plotHeight <= 0) return [];

    const buildPaths = (accessor, projectY) => getDefinedRuns(points, accessor)
      .map(run => buildSmoothPath(run.map(point => [
        getX(points.indexOf(point)),
        projectY(accessor(point)),
      ])))
      .filter(Boolean);

    return [
      {
        key: 'potential',
        color: colors.potential,
        dashed: false,
        paths: buildPaths(point => point.potential, getLeftY),
      },
      {
        key: 'heatDelivered',
        color: colors.heatDelivered,
        dashed: true,
        paths: buildPaths(point => point.heatDelivered, getLeftY),
      },
      {
        key: 'deltaT',
        color: colors.deltaT,
        dashed: false,
        paths: buildPaths(point => point.deltaT, getRightY),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, plotWidth, plotHeight, leftScale, rightScale]);

  const hoveredLocalIndex = hoveredId == null
    ? -1
    : points.findIndex(point => String(point.id) === String(hoveredId));
  const hoveredPoint = hoveredLocalIndex >= 0 ? points[hoveredLocalIndex] : undefined;

  const getPointAtEvent = (event) => {
    if (points.length === 0 || plotWidth <= 0) return;

    const bounds = containerRef.current.getBoundingClientRect();
    const relativeX = event.clientX - bounds.left - margin.left;
    const ratio = Math.min(1, Math.max(0, relativeX / plotWidth));
    const localIndex = Math.round(ratio * (points.length - 1));
    return points[localIndex];
  };
  const handleMouseMove = (event) => {
    const point = getPointAtEvent(event);
    if (point) onHover(point.id);
  };
  const handleClick = (event) => {
    const point = getPointAtEvent(event);
    if (point) onClick(point.id);
  };

  const hoveredX = hoveredPoint ? getX(hoveredLocalIndex) : 0;
  const tooltipOnRight = hoveredX > margin.left + plotWidth / 2;

  return (
    <div className="potential-chart" ref={containerRef}>
      {width > 0 && height > 0 && (
        <svg
          className="potential-chart__svg"
          width={width}
          height={height}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => onHover(null)}
          onClick={handleClick}
        >
          <text
            className="potential-chart__axis-unit potential-chart__axis-unit--left"
            x={22}
            y={margin.top + plotHeight / 2}
            transform={`rotate(-90 22 ${margin.top + plotHeight / 2})`}
          >
            GWh/jaar
          </text>
          {leftScale.ticks.map(tick => (
            <g key={`left-${tick}`}>
              <line
                className="potential-chart__gridline"
                x1={margin.left}
                x2={margin.left + plotWidth}
                y1={getLeftY(tick)}
                y2={getLeftY(tick)}
              />
              <text
                className="potential-chart__axis-label potential-chart__axis-label--left"
                x={margin.left - 8}
                y={getLeftY(tick)}
              >
                {valueFormatter.format(tick)}
              </text>
            </g>
          ))}
          {rightScale.ticks.map(tick => (
            <text
              key={`right-${tick}`}
              className="potential-chart__axis-label potential-chart__axis-label--right"
              x={margin.left + plotWidth + 8}
              y={getRightY(tick)}
            >
              {`${valueFormatter.format(tick)} °C`}
            </text>
          ))}

          {hoveredPoint && (
            <line
              className="potential-chart__hover-line"
              x1={hoveredX}
              x2={hoveredX}
              y1={margin.top}
              y2={margin.top + plotHeight}
            />
          )}

          {series.map(serie => serie.paths.map((path, pathIndex) => (
            <path
              key={`${serie.key}-${pathIndex}`}
              className="potential-chart__line"
              d={path}
              fill="none"
              stroke={serie.color}
              strokeWidth={2}
              strokeDasharray={serie.dashed ? '6 5' : undefined}
            />
          )))}

          {hoveredPoint && (
            <>
              {hoveredPoint.potential !== undefined && (
                <circle
                  className="potential-chart__marker"
                  cx={hoveredX}
                  cy={getLeftY(hoveredPoint.potential)}
                  r={3.5}
                  style={{ fill: colors.potential }}
                />
              )}
              {hoveredPoint.heatDelivered !== undefined && (
                <circle
                  className="potential-chart__marker"
                  cx={hoveredX}
                  cy={getLeftY(hoveredPoint.heatDelivered)}
                  r={3.5}
                  style={{ fill: colors.heatDelivered }}
                />
              )}
              {hoveredPoint.deltaT !== undefined && (
                <circle
                  className="potential-chart__marker"
                  cx={hoveredX}
                  cy={getRightY(hoveredPoint.deltaT)}
                  r={3.5}
                  style={{ fill: colors.deltaT }}
                />
              )}
            </>
          )}
        </svg>
      )}

      {hoveredPoint && (
        <div
          className={`potential-chart__tooltip${tooltipOnRight ? ' potential-chart__tooltip--left' : ''}`}
          style={{ left: `${hoveredX}px`, top: `${margin.top}px` }}
        >
          <div className="potential-chart__tooltip-row">
            <span className="potential-chart__tooltip-swatch" style={{ background: colors.potential }} />
            <span className="potential-chart__tooltip-label">Bronpotentieel</span>
            <span className="potential-chart__tooltip-value">{formatEnergyPerYear(hoveredPoint.potential)}</span>
          </div>
          <div className="potential-chart__tooltip-row">
            <span className="potential-chart__tooltip-swatch potential-chart__tooltip-swatch--dashed" style={{ color: colors.heatDelivered }} />
            <span className="potential-chart__tooltip-label">Vermogen geleverd</span>
            <span className="potential-chart__tooltip-value">{formatEnergyPerYear(hoveredPoint.heatDelivered)}</span>
          </div>
          <div className="potential-chart__tooltip-row">
            <span className="potential-chart__tooltip-swatch" style={{ background: colors.deltaT }} />
            <span className="potential-chart__tooltip-label">DeltaT</span>
            <span className="potential-chart__tooltip-value">{formatTemperature(hoveredPoint.deltaT)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export { colors };
export default PotentialChart;
